import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BACKEND_URL}/api`;

// Phase 4-b: cookie-based session, no more X-Session-Id header injection.
// Cookies are httpOnly + SameSite=None + Secure; axios needs withCredentials.
export const api = axios.create({
    baseURL: API_BASE,
    timeout: 180000,
    withCredentials: true,
});

// One-off legacy session_id reader — used only by the LegacyClaimBanner so
// we can offer to merge old anonymous data into the new account.
const LEGACY_KEY = "smartpublish_session_id";
export function getLegacySessionId() {
    try {
        return window.localStorage.getItem(LEGACY_KEY);
    } catch {
        return null;
    }
}
export function clearLegacySessionId() {
    try {
        window.localStorage.removeItem(LEGACY_KEY);
    } catch {
        /* ignore */
    }
}

// Centralized auth/tier/balance error routing. SessionProvider installs the
// interceptor at app boot via `installApiErrorHandlers`. We re-install (eject
// old + add new) whenever the registered handlers change so closures stay
// fresh. Returns the eject function for cleanup.
//
// Skip handling for paths we want to handle manually inside the caller —
// e.g. `/me` (silent 401 = anonymous) or `/auth/session` (callback flow).
const SILENT_PATHS = [
    "/me",
    "/auth/session",
    "/auth/me",
    "/auth/legacy-check",
];

function isSilentPath(url) {
    if (!url) return false;
    return SILENT_PATHS.some((p) => url.includes(p));
}

export function installApiErrorHandlers({
    onUnauthenticated,
    onTrialExpired,
    onTierRequired,
} = {}) {
    const id = api.interceptors.response.use(
        (resp) => resp,
        (err) => {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const code =
                (detail && typeof detail === "object" && detail.code) || null;
            const url = err?.config?.url || "";

            if (status === 401 && !isSilentPath(url)) {
                onUnauthenticated?.();
            } else if (status === 402 && code === "trial_expired") {
                onTrialExpired?.(detail);
            } else if (status === 403 && code === "tier_required") {
                onTierRequired?.(detail);
            }
            return Promise.reject(err);
        }
    );
    return () => api.interceptors.response.eject(id);
}
